import { buildDocumentPrintStyles } from './documentPrintStyles';

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

export const waitForDocumentResources = async (target, { fallbackDelay = 2000 } = {}) => {
  if (!target?.document) return;

  const images = Array.from(target.document.images || []);
  const imagesReady = Promise.all(images.map((image) => {
    if (image.complete) return Promise.resolve();
    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }));

  const fontsReady = target.document.fonts?.ready || Promise.resolve();
  const fallback = new Promise((resolve) => setTimeout(resolve, fallbackDelay));
  await Promise.race([Promise.all([imagesReady, fontsReady]), fallback]);
};

export const buildDocumentPrintHtml = async ({
  element,
  title = 'Document',
  orientation = 'portrait',
  styles = '',
}) => {
  const { renderToStaticMarkup } = await import('react-dom/server');
  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${buildDocumentPrintStyles(orientation)}${styles || ''}</style></head><body>${renderToStaticMarkup(element)}</body></html>`;
};

export const printDocument = async ({
  element,
  title = 'Document',
  orientation = 'portrait',
  styles = '',
  windowFeatures = 'width=1280,height=800',
}) => {
  const target = window.open('', '_blank', windowFeatures);
  if (!target) {
    throw new Error("Impossible d’ouvrir la fenêtre d’impression. Vérifiez le blocage des pop-ups.");
  }

  target.document.open();
  target.document.write(await buildDocumentPrintHtml({ element, title, orientation, styles }));
  target.document.close();
  target.focus();
  target.requestAnimationFrame(() => target.print());
  return title;
};


export const printCurrentDocument = async ({
  target = window,
  waitForResources = true,
  resourcesFallbackDelay = 2000,
} = {}) => {
  if (!target?.document) {
    throw new Error("Impossible d’accéder au document à imprimer.");
  }

  if (waitForResources) {
    await waitForDocumentResources(target, { fallbackDelay: resourcesFallbackDelay });
  }

  target.focus?.();
  target.requestAnimationFrame(() => target.print());
  return true;
};

export const openHtmlDocument = async ({
  html,
  print = false,
  windowFeatures = 'width=1024,height=768',
  printDelay = 300,
  waitForResources = false,
  resourcesFallbackDelay = 2000,
  targetWindow = null,
}) => {
  const target = targetWindow || window.open('', '_blank', windowFeatures);
  if (!target) {
    throw new Error("Impossible d’ouvrir la fenêtre du document. Vérifiez le blocage des pop-ups.");
  }

  target.document.open();
  target.document.write(html);
  target.document.close();
  target.focus();

  if (waitForResources) {
    await waitForDocumentResources(target, { fallbackDelay: resourcesFallbackDelay });
  }

  if (print) {
    if (waitForResources) {
      target.requestAnimationFrame(() => target.print());
    } else {
      setTimeout(() => target.print(), printDelay);
    }
  }

  return target;
};
